// Settings → License & Subscription
//
// Tenant-facing view of the org's licensing-v2 state. Consumes three
// endpoints (no API work needed):
//
//   GET  /api/user/v1/licensing/status
//        -> { org_id, skus: [...], active_breaches: [...] }
//   GET  /api/user/v1/licensing/effective-features
//        -> { org_id, features: { <key>: true|false|number|null } }
//   POST /api/user/v1/licensing/request-quota   { breach_id, message }
//        -> { ok, message }
//
// This file is a redesign against the License & Subscription mock at
// /home/ubuntu/license-design. Layout:
//
//   - Page head: gradient indigo mark + title + subtitle + Refresh.
//   - Status banner: green health pill + last-synced timestamp, with
//     an amber expiring-soon pill when any SKU lands within 30 days.
//   - Active subscriptions: 2-column grid of premium cards (accent
//     ribbon, SKU icon, Enterprise/Trial badge with crown, expiry
//     chip with state (ok/soon), per-dimension quota meters that
//     flip to a warn fill + Request-more-quota CTA when an active
//     breach is recorded for that dimension).
//   - Effective feature flags: searchable + filterable table grouped
//     by SKU prefix (Web Security / API Security / AI·LLM / Platform).
//     Numeric values render as mono numeric pills; null renders as
//     a dim "Not set" pill.
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, Button, CircularProgress, IconButton,
  Dialog, DialogContent, DialogActions, InputAdornment, Select, MenuItem,
  Alert as MuiAlert} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import {
  ShieldCheck, Braces, Sparkles, Settings2,
  MessagesSquare, Check, AlarmClock,
  CalendarClock, CircleCheck, Crown, Infinity as InfinityIcon,
  Filter, ArrowRight, X,
} from "lucide-react";

import V2PageHeader from "../../../common/V2PageHeader";
import LayerdIcon from "../../../common/LayerdIcon";
import RefreshIconButton from "../../../common/RefreshIconButton";
import { CancelButton, PrimaryButton } from "../../../common/Button";
import { SafeTextField } from "../../../common/Greens";
import userAxios from "../../../../utils/axios/v1/userAxios";
import colors from "../../../../utils/colors";
import { useNotification } from "../../../../contexts/NotificationContext";
import { filterInputSx } from "../services/api_security/asecUi";

const brandBlue = colors.primary[300];

const PLAN_BADGE = {
  enterprise: {
    bg: colors.primary.alpha,
    ink: colors.primary[500],
    border: "#CED2F2",
    crown: colors.yellow[200],
  },
  trial: {
    bg: "#EDF3FE",
    ink: "#0255ED",
    border: "#C7DAFB",
    crown: "#3577F1",
  },
};

const FILTER_CONTROL_HEIGHT = 36;

const EVENT_FILTER_BAR_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: 2,
  backgroundColor: "white",
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
};

const filterOutlinedRootSx = {
  height: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  maxHeight: FILTER_CONTROL_HEIGHT,
  borderRadius: "8px",
  fontSize: 14,
  backgroundColor: "#fff",
  boxSizing: "border-box",
  "& fieldset": { borderColor: colors.neutral[200] },
  "&:hover fieldset": { borderColor: colors.neutral[300] },
  "&.Mui-focused fieldset": { borderColor: brandBlue },
};

const filterSelectSx = {
  height: FILTER_CONTROL_HEIGHT,
  "& .MuiOutlinedInput-root": filterOutlinedRootSx,
  "& .MuiSelect-select": {
    py: 0,
    pl: 1.5,
    pr: 4,
    height: `${FILTER_CONTROL_HEIGHT}px !important`,
    minHeight: `${FILTER_CONTROL_HEIGHT}px !important`,
    fontSize: 14,
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
    right: 8,
  },
};

// ── Layout helpers ─────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch (_) { return iso; }
}

function daysBetween(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function formatExpiryNote(iso) {
  const d = daysBetween(iso);
  if (d === null) return null;
  if (d < 0) return "Expired";
  if (d === 0) return "Expires today";
  if (d === 1) return "1 day left";
  if (d < 60) return `${d} days left`;
  const months = Math.round(d / 30);
  if (months < 12) return `${months} months left`;
  const years = Math.round(d / 365);
  return years === 1 ? "1 yr left" : `${years} yrs left`;
}

function expiryState(iso) {
  const d = daysBetween(iso);
  if (d === null || d < 0) return "soon";
  return d <= 30 ? "soon" : "ok";
}

function formatNumber(n) {
  if (n === null || n === undefined) return null;
  if (typeof n !== "number") return String(n);
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return n.toLocaleString();
}

function formatBytes(n) {
  if (n === null || n === undefined) return null;
  if (n >= 1024 ** 4) return (n / 1024 ** 4).toFixed(1).replace(/\.0$/, "") + " TB";
  if (n >= 1024 ** 3) return (n / 1024 ** 3).toFixed(1).replace(/\.0$/, "") + " GB";
  if (n >= 1024 ** 2) return (n / 1024 ** 2).toFixed(1).replace(/\.0$/, "") + " MB";
  if (n >= 1024) return (n / 1024).toFixed(1).replace(/\.0$/, "") + " KB";
  return n + " B";
}

function formatBreachDimension(dimension) {
  const labels = {
    bytes: "Monthly byte budget",
    byte_count: "Monthly byte budget",
    requests: "Monthly request quota",
    request_count: "Monthly request quota",
  };
  return labels[dimension] || String(dimension || "Quota").replace(/_/g, " ");
}

// Map SKU names to the design's icon + description vocabulary.
const SKU_META = {
  "Web Security": {
    Icon: ShieldCheck,
    desc: "WAF, signature engine, SmartParse, DDoS, bot management.",
  },
  "API Security": {
    Icon: Braces,
    desc: "OpenAPI validation, DLP, GraphQL guard, auth platform.",
  },
  "AI Security": {
    Icon: Sparkles,
    desc: "AI bot detection, LLM-crawler controls, agentic policy.",
  },
};

// Map feature-key prefixes to the design's flag-group vocabulary.
const FLAG_GROUPS_META = [
  { key: "waf",      title: "Web Security", Icon: ShieldCheck },
  { key: "api",      title: "API Security", Icon: Braces },
  { key: "ai",       title: "AI / LLM",     Icon: Sparkles },
  { key: "platform", title: "Platform",     Icon: Settings2 },
];

function groupFlags(features) {
  const groups = FLAG_GROUPS_META.map((g) => ({ ...g, flags: [] }));
  const other = { key: "other", title: "Other", Icon: Settings2, flags: [] };
  for (const [k, v] of Object.entries(features || {})) {
    const prefix = k.split(".")[0];
    const g = groups.find((x) => x.key === prefix);
    (g || other).flags.push({ key: k, value: v });
  }
  for (const g of groups) g.flags.sort((a, b) => a.key.localeCompare(b.key));
  other.flags.sort((a, b) => a.key.localeCompare(b.key));
  return [...groups.filter((g) => g.flags.length), ...(other.flags.length ? [other] : [])];
}

// ── UI bits ────────────────────────────────────────────────────

const flagKeySx = { fontSize: 14, lineHeight: "21px" };

function KeyLabel({ k }) {
  const dot = k.lastIndexOf(".");
  if (dot === -1) {
    return <Box component="span" sx={{ ...flagKeySx, color: colors.neutral[1000] }}>{k}</Box>;
  }
  return (
    <Box component="span" sx={flagKeySx}>
      <Box component="span" sx={{ color: colors.neutral[600] }}>{k.slice(0, dot + 1)}</Box>
      <Box component="span" sx={{ color: colors.neutral[1000] }}>{k.slice(dot + 1)}</Box>
    </Box>
  );
}

function FlagValue({ v }) {
  const baseSx = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.75,
    minWidth: 64, height: 26, px: 1.5, borderRadius: 999,
    fontSize: 12, fontWeight: 600,
  };
  if (v === true) {
    return (
      <Box sx={{ ...baseSx,
        bgcolor: "#F6FCED",
        color: "#1FC16B",
        border: "1px solid #DBF3B5",
      }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#1FC16B" }} />
        On
      </Box>
    );
  }
  if (v === false) {
    return (
      <Box sx={{ ...baseSx,
        bgcolor: "#fff",
        color: colors.neutral[700],
        border: `1px solid ${colors.neutral[300]}`,
      }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: colors.neutral[500] }} />
        Off
      </Box>
    );
  }
  if (v === null || v === undefined) {
    return (
      <Box sx={{ ...baseSx,
        bgcolor: "transparent",
        color: colors.neutral[500],
        fontWeight: 500,
      }}>
        Not set
      </Box>
    );
  }
  return (
    <Box sx={{ ...baseSx,
      bgcolor: "rgba(2,85,237,0.07)",
      color: "#0244BE",
      border: "1px solid rgba(2,85,237,0.20)",
    }}>
      {typeof v === "number" ? v.toLocaleString() : String(v)}
    </Box>
  );
}

function Meter({ title, cap, capLabel, breached, onRequestMore }) {
  if (cap === null || cap === undefined) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1.5} sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: 500, color: colors.neutral[700] }}>{title}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: brandBlue }}>
            <InfinityIcon size={16} />
            <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: 600, color: brandBlue }}>Unlimited</Typography>
          </Stack>
        </Stack>
        <Box sx={{
          height: 6,
          borderRadius: 999,
          background: `repeating-linear-gradient(90deg, ${colors.neutral[200]} 0 7px, transparent 7px 14px)`,
        }} />
      </Box>
    );
  }
  const fillPct = breached ? 100 : 5;  // we don't have live usage from the API
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1.5} sx={{ mb: 1.25 }}>
        <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: 500, color: colors.neutral[700] }}>{title}</Typography>
        <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[600], whiteSpace: "nowrap" }}>
          <Box component="b" sx={{ color: colors.neutral[1000], fontWeight: 600 }}>{breached ? "Over cap" : "Cap"}</Box> {breached ? "·" : "·"} {capLabel}
        </Typography>
      </Stack>
      <Box sx={{ height: 6, borderRadius: 999, bgcolor: colors.neutral[200], overflow: "hidden" }}>
        <Box sx={{
          height: "100%",
          width: `${fillPct}%`,
          borderRadius: 999,
          bgcolor: breached ? colors.yellow[200] : brandBlue,
          transition: "width 0.5s ease",
        }} />
      </Box>
      {breached && onRequestMore && (
        <Button
          size="small"
          variant="text"
          onClick={onRequestMore}
          endIcon={<ArrowRight size={14} strokeWidth={2.25} />}
          sx={{
            mt: 1.25,
            alignSelf: "flex-start",
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            color: brandBlue,
            p: 0,
            minWidth: 0,
            "& .MuiButton-endIcon": { ml: 0.375, color: "inherit" },
            "&:hover": {
              background: "transparent",
              textDecoration: "underline",
              color: colors.primary[400],
            },
          }}
        >
          Request more quota
        </Button>
      )}
    </Box>
  );
}

function PlanBadge({ isTrial }) {
  const style = isTrial ? PLAN_BADGE.trial : PLAN_BADGE.enterprise;
  return (
    <Box sx={{
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      height: 28,
      px: 1.5,
      borderRadius: 999,
      bgcolor: style.bg,
      color: style.ink,
      border: `1px solid ${style.border}`,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.01em",
      lineHeight: 1,
      boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
    }}>
      <Crown size={13} color={style.crown} strokeWidth={2.25} />
      {isTrial ? "Trial" : "Enterprise"}
    </Box>
  );
}

function ExpiryChip({ iso }) {
  const state = expiryState(iso);
  const note = formatExpiryNote(iso);
  if (!note) return null;
  const styles = state === "soon"
    ? { bg: "#FFF6D0", border: "#F5C354", color: "#916C19", Icon: AlarmClock }
    : { bg: "#F6FCED", border: "#DBF3B5", color: "#1FC16B", Icon: CircleCheck };
  const Icon = styles.Icon;
  return (
    <Box sx={{
      ml: "auto", flexShrink: 0,
      display: "inline-flex", alignItems: "center", gap: 0.625, height: 25, px: 1.375,
      borderRadius: 999, fontSize: 12, fontWeight: 600,
      bgcolor: styles.bg, color: styles.color, border: `1px solid ${styles.border}`,
      whiteSpace: "nowrap",
    }}>
      <Icon size={13} />
      {note}
    </Box>
  );
}

function SubCard({ sku, breaches, onRequestMore }) {
  const meta = SKU_META[sku.sku_name] || { Icon: ShieldCheck, desc: "" };
  const Icon = meta.Icon;
  const breachByDim = useMemo(() => {
    const m = {};
    for (const b of breaches) m[b.dimension] = b;
    return m;
  }, [breaches]);
  const reqBreach = breachByDim.requests || breachByDim.request_count || null;
  const byteBreach = breachByDim.bytes || breachByDim.byte_count || null;

  return (
    <Box sx={{
      position: "relative",
      bgcolor: "#fff",
      border: `1px solid ${colors.neutral[200]}`,
      borderRadius: "12px",
      overflow: "hidden",
      transition: "box-shadow 0.18s",
      "&:hover": { boxShadow: "0 4px 16px rgba(17,17,17,0.06)" },
      opacity: sku.expired ? 0.7 : 1,
    }}>
      <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{
            width: 48, height: 48, borderRadius: "10px",
            display: "grid", placeItems: "center",
            bgcolor: colors.primary[100], color: brandBlue,
            flexShrink: 0,
          }}>
            <Icon size={24} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
              sx={{ mb: 1.25 }}
            >
              <Typography sx={{ fontSize: 15, lineHeight: "22px", fontWeight: 600, color: colors.neutral[1000] }}>
                {sku.sku_name}
              </Typography>
              <PlanBadge isTrial={sku.is_trial} />
            </Stack>
            <Typography sx={{
              fontSize: 14,
              lineHeight: 1.6,
              color: colors.neutral[600],
              maxWidth: 520,
            }}>
              {meta.desc}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ height: "1px", bgcolor: colors.neutral[200], my: 3 }} />

        <Box sx={{
          p: 2,
          borderRadius: "10px",
          bgcolor: colors.neutral[50],
          border: `1px solid ${colors.neutral[200]}`,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.75}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "8px",
              display: "grid", placeItems: "center",
              bgcolor: "#fff", color: colors.neutral[700],
              border: `1px solid ${colors.neutral[200]}`,
              flexShrink: 0,
            }}>
              <CalendarClock size={18} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 600,
                letterSpacing: "0.06em", textTransform: "uppercase",
                color: colors.neutral[500],
              }}>
                Expires
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: colors.neutral[1000], mt: 0.5 }}>
                {formatDate(sku.expiry_date)}
              </Typography>
            </Box>
            <ExpiryChip iso={sku.expiry_date} />
          </Stack>
        </Box>

        <Box sx={{
          mt: 2.5,
          p: 2.25,
          borderRadius: "10px",
          bgcolor: colors.neutral[50],
          border: `1px solid ${colors.neutral[200]}`,
        }}>
          <Stack spacing={3}>
            <Meter
              title="Monthly request quota"
              cap={sku.volume_limit_requests}
              capLabel={formatNumber(sku.volume_limit_requests) || "Unlimited"}
              breached={!!reqBreach}
              onRequestMore={reqBreach && !reqBreach.request_pending ? () => onRequestMore(reqBreach) : null}
            />
            <Meter
              title="Monthly byte budget"
              cap={sku.volume_limit_bytes}
              capLabel={formatBytes(sku.volume_limit_bytes) || "Unlimited"}
              breached={!!byteBreach}
              onRequestMore={byteBreach && !byteBreach.request_pending ? () => onRequestMore(byteBreach) : null}
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

const FLAG_FILTER_LABELS = { all: "All", on: "Enabled", off: "Disabled" };

function FeatureFlagsFilterBar({ query, setQuery, filter, setFilter }) {
  return (
    <Box sx={EVENT_FILTER_BAR_SX}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LayerdIcon icon={Filter} size={18} color="primary" />
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
            Filters
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5, flex: 1 }}>
          <SafeTextField
            placeholder="Filter keys…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            sx={{ ...filterInputSx, width: { xs: "100%", sm: 280 }, flex: { sm: "1 1 280px" }, maxWidth: { sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ ...filterSelectSx, minWidth: 150 }}
            renderValue={(v) => `Status: ${FLAG_FILTER_LABELS[v] || v}`}
          >
            {Object.entries(FLAG_FILTER_LABELS).map(([k, lbl]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 14 }}>{lbl}</MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>
    </Box>
  );
}

function FlagsTable({ groups, query, setQuery, filter, setFilter, totalCount, shownCount }) {
  return (
    <Stack spacing={2}>
      <FeatureFlagsFilterBar
        query={query}
        setQuery={setQuery}
        filter={filter}
        setFilter={setFilter}
      />

      <Box sx={{
        bgcolor: "#fff",
        border: `1px solid ${colors.neutral[200]}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}>
        <Stack
          direction="row" alignItems="center" spacing={1.5}
          sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
            Feature flags
          </Typography>
          <Box sx={{
            fontSize: 12, fontWeight: 500,
            color: colors.neutral[700],
            bgcolor: colors.neutral[100],
            borderRadius: 999, px: 1.25, py: "2px",
          }}>
            {shownCount === totalCount ? `${totalCount}` : `${shownCount} / ${totalCount}`}
          </Box>
        </Stack>

        {groups.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center", color: colors.neutral[600], fontSize: 14 }}>
            No feature flags match {query ? `"${query}"` : "the current filter"}.
          </Box>
        ) : (
          groups.map((g) => {
            const GIcon = g.Icon;
            return (
              <React.Fragment key={g.key}>
                <Stack direction="row" alignItems="center" spacing={1.125} sx={{
                  px: 3, py: 1.5,
                  bgcolor: colors.neutral[50],
                  borderBottom: `1px solid ${colors.neutral[200]}`,
                }}>
                  <GIcon size={15} color={colors.neutral[600]} />
                  <Typography sx={{
                    fontSize: 14,
                    lineHeight: "21px",
                    fontWeight: "medium",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: colors.neutral[700],
                  }}>
                    {g.title}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[500] }}>
                    {g.flags.length}
                  </Typography>
                </Stack>
                {g.flags.map((f) => (
                  <Stack key={f.key} direction="row" alignItems="center" sx={{
                    px: 3, py: 1.75,
                    borderBottom: `1px solid ${colors.neutral[200]}`,
                    "&:hover": { bgcolor: colors.neutral[50] },
                    "&:last-of-type": { borderBottom: "none" },
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <KeyLabel k={f.key} />
                    </Box>
                    <FlagValue v={f.value} />
                  </Stack>
                ))}
              </React.Fragment>
            );
          })
        )}
      </Box>
    </Stack>
  );
}

// ── Page ───────────────────────────────────────────────────────

export default function LicensePage() {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [features, setFeatures] = useState({});
  const [syncedAt, setSyncedAt] = useState(null);
  const [error, setError] = useState(null);
  const [requestBreach, setRequestBreach] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [requesting, setRequesting] = useState(false);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const statusRes = await userAxios.get("/licensing/status");
      setStatus(statusRes.data);
      setSyncedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
      setStatus(null);
    } finally {
      setLoading(false);
    }
    userAxios.get("/licensing/effective-features")
      .then((r) => setFeatures(r.data.features || {}))
      .catch(() => setFeatures({}));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const filteredGroups = useMemo(() => {
    const all = groupFlags(features);
    const q = query.trim().toLowerCase();
    return all
      .map((g) => ({
        ...g,
        flags: g.flags.filter((f) => {
          if (q && !f.key.toLowerCase().includes(q)) return false;
          if (filter === "on" && f.value !== true) return false;
          if (filter === "off" && !(f.value === false || f.value == null)) return false;
          return true;
        }),
      }))
      .filter((g) => g.flags.length > 0);
  }, [features, query, filter]);

  const totalFlags = Object.keys(features).length;
  const shownFlags = filteredGroups.reduce((n, g) => n + g.flags.length, 0);

  const expiringSoon = useMemo(() => {
    if (!status) return null;
    return status.skus.find((s) => {
      const d = daysBetween(s.expiry_date);
      return d !== null && d >= 0 && d <= 30;
    });
  }, [status]);

  const breachesByOrgSku = useMemo(() => {
    const m = {};
    for (const b of (status?.active_breaches || [])) {
      if (!m[b.sku_id]) m[b.sku_id] = [];
      m[b.sku_id].push(b);
    }
    return m;
  }, [status]);

  const handleRequestSubmit = async () => {
    if (!requestBreach) return;
    setRequesting(true);
    try {
      const res = await userAxios.post("/licensing/request-quota", {
        breach_id: requestBreach.id,
        message: requestMessage || undefined,
      });
      showSuccess(res.data?.message || "Request submitted.");
      setRequestBreach(null);
      setRequestMessage("");
      refresh();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setRequesting(false);
    }
  };

  const syncedAgo = (() => {
    if (!syncedAt) return null;
    const sec = Math.round((Date.now() - syncedAt.getTime()) / 1000);
    if (sec < 60) return "just now";
    if (sec < 3600) {
      const m = Math.round(sec / 60);
      return `${m} minute${m === 1 ? "" : "s"} ago`;
    }
    return new Date(syncedAt).toLocaleTimeString();
  })();

  return (
    <>
      <Helmet title="License & Subscription" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <V2PageHeader
              title="License & Subscription"
              description="Review active plans, monitor quota usage, and inspect effective feature flags."
              sx={{ pb: 0 }}
              actions={<RefreshIconButton onClick={refresh} disabled={loading} loading={loading} />}
            />
          </Stack>

        {loading && !status && (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: brandBlue }} />
          </Box>
        )}

        {error && (
          <MuiAlert severity="error" sx={{ mb: 3 }}>
            Could not load license status: {error}
          </MuiAlert>
        )}

        {status && (
          <>
            {/* Status bar */}
            <Stack
              direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap"
              sx={{
                p: 2,
                bgcolor: "#fff",
                border: `1px solid ${colors.neutral[200]}`,
                borderRadius: "8px",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "8px",
                  display: "grid", placeItems: "center",
                  bgcolor: status.active_breaches.length === 0 ? colors.secondary.alpha : colors.yellow[10],
                  color: status.active_breaches.length === 0 ? colors.green[200] : colors.yellow[200],
                }}>
                  {status.active_breaches.length === 0 ? <Check size={17} /> : <AlarmClock size={17} />}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], display: "block" }}>
                    {status.active_breaches.length === 0
                      ? "All subscriptions are within their quotas."
                      : `${status.active_breaches.length} active breach${status.active_breaches.length === 1 ? "" : "es"}.`}
                  </Typography>
                  {syncedAgo && (
                    <Typography sx={{ fontSize: 13, color: colors.neutral[600], mt: 0.25 }}>
                      Last synced {syncedAgo}.
                    </Typography>
                  )}
                </Box>
              </Stack>

              {expiringSoon && (
                <Stack
                  direction="row" alignItems="center" spacing={1.125}
                  sx={{
                    ml: "auto",
                    pl: 1.5, pr: 1,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: colors.yellow[10],
                    border: `1px solid ${colors.yellow[200]}`,
                  }}
                >
                  <AlarmClock size={15} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.neutral[800] }}>
                    {expiringSoon.sku_name} renews in {formatExpiryNote(expiringSoon.expiry_date)?.replace(" left", "")}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {/* Subscriptions section */}
            <Stack spacing={3}>
            <Stack direction="row" alignItems="baseline" spacing={1.25}>
              <Typography sx={{
                fontSize: 15,
                lineHeight: "21px",
                fontWeight: "medium",
                color: colors.neutral[1000],
              }}>
                Active subscriptions
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[500] }}>
                {status.skus.length} {status.skus.length === 1 ? "plan" : "plans"}
              </Typography>
            </Stack>

            {status.skus.length === 0 ? (
              <MuiAlert severity="info">
                No active subscriptions on this organisation. Contact your account manager to enable Layerd security SKUs.
              </MuiAlert>
            ) : (
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gap: 3,
              }}>
                {status.skus.map((sku) => (
                  <SubCard
                    key={sku.sku_id}
                    sku={sku}
                    breaches={breachesByOrgSku[sku.sku_id] || []}
                    onRequestMore={(b) => { setRequestBreach(b); setRequestMessage(""); }}
                  />
                ))}
              </Box>
            )}
            </Stack>

            {/* Feature flags section */}
            <Stack spacing={2}>
              <Stack direction="row" alignItems="baseline" spacing={1.25}>
                <Typography sx={{
                  fontSize: 15,
                  lineHeight: "21px",
                  fontWeight: "medium",
                  color: colors.neutral[1000],
                }}>
                  Effective feature flags
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[500] }}>
                  resolved across all plans
                </Typography>
              </Stack>

              <FlagsTable
                groups={filteredGroups}
                query={query}
                setQuery={setQuery}
                filter={filter}
                setFilter={setFilter}
                totalCount={totalFlags}
                shownCount={shownFlags}
              />
            </Stack>
          </>
        )}
        </Stack>
      </Box>

      {/* Request-more-quota dialog */}
      <Dialog
        open={!!requestBreach}
        onClose={() => !requesting && setRequestBreach(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(17,17,17,0.45)" } },
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 16px 48px rgba(17,17,17,0.14)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 3.5, pt: 3.5, pb: 0 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                bgcolor: colors.primary[100],
                color: brandBlue,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}>
                <MessagesSquare size={22} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: "26px", color: colors.neutral[1000] }}>
                  Request more quota
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: colors.neutral[600], mt: 0.75 }}>
                  Share context for your account manager. They&apos;ll receive a notification as soon as you submit.
                </Typography>
              </Box>
            </Stack>
            <IconButton
              aria-label="Close"
              onClick={() => !requesting && setRequestBreach(null)}
              disabled={requesting}
              sx={{
                mt: -0.5,
                mr: -0.5,
                color: colors.neutral[600],
                "&:hover": { bgcolor: colors.neutral[50] },
              }}
            >
              <X size={18} />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ px: 3.5, pt: 3, pb: 0 }}>
          {requestBreach && (() => {
            const skuMeta = SKU_META[requestBreach.sku_name] || { Icon: ShieldCheck };
            const SkuIcon = skuMeta.Icon;
            const dimensionLabel = formatBreachDimension(requestBreach.dimension);

            return (
              <Stack spacing={3}>
                <Box sx={{
                  p: 2,
                  borderRadius: "10px",
                  bgcolor: colors.neutral[50],
                  border: `1px solid ${colors.neutral[200]}`,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1.75}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      bgcolor: "#fff",
                      color: brandBlue,
                      border: `1px solid ${colors.neutral[200]}`,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}>
                      <SkuIcon size={20} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: colors.neutral[500],
                      }}>
                        Quota increase for
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: colors.neutral[1000], mt: 0.375 }}>
                        {requestBreach.sku_name}
                      </Typography>
                    </Box>
                    <Box sx={{
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.625,
                      height: 26,
                      px: 1.25,
                      borderRadius: 999,
                      bgcolor: "#FFF6D0",
                      color: "#916C19",
                      border: "1px solid #F5C354",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}>
                      <AlarmClock size={13} />
                      {dimensionLabel}
                    </Box>
                  </Stack>
                </Box>

                <Box>
                  <SafeTextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Context for your account manager (optional)"
                    placeholder="e.g. We're launching a campaign next week and expect 3× normal traffic for 14 days."
                    value={requestMessage}
                    onChange={(e) => setRequestMessage(e.target.value)}
                    inputProps={{ maxLength: 1000 }}
                    disabled={requesting}
                    helperText={null}
                  />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 12.5, lineHeight: "18px", color: colors.neutral[500] }}>
                      Optional — helps your account manager prioritize the request.
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>
                      {requestMessage.length}/1000
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{
          px: 3.5,
          py: 3,
          pt: 2.5,
          gap: 1.5,
          borderTop: `1px solid ${colors.neutral[200]}`,
          bgcolor: colors.neutral[50],
        }}>
          <CancelButton onClick={() => setRequestBreach(null)} disabled={requesting} sx={{ width: "auto", minWidth: 104 }} />
          <PrimaryButton
            onClick={handleRequestSubmit}
            disabled={requesting}
            startIcon={requesting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ width: "auto", minWidth: 148 }}
          >
            {requesting ? "Submitting…" : "Submit request"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

    </>
  );
}
